/**
 * Lightweight telemetry for the ProductCard `prefetchNext` warm path.
 *
 * Uses the Performance API to measure how often the prefetch actually fires,
 * how long the prefetched image takes to arrive, and, as a rough baseline, 
 * how long comparable product images that were NOT prefetched take to load.
 * The delta is the "cache-warming benefit" the prefetch produces when the
 * user actually navigates to a neighboring product card.
 *
 * Data is exposed on `window.__prefetchStats` so it can be sampled from
 * DevTools, wired into an analytics beacon, or asserted in a Playwright run.
 */

type ImageLoadRecord = {
 url: string;
 duration: number;
 fromCache: boolean;
 ts: number;
};

interface PrefetchStats {
 /** How many times warmNext fired (deduped per ProductCard instance). */
 fireCount: number;
 /** URLs the app asked the browser to prefetch. */
 prefetchedUrls: Set<string>;
 /** Image resource loads that matched a prior prefetch. */
 prefetchedLoads: ImageLoadRecord[];
 /** Image resource loads that did NOT match a prior prefetch (baseline). */
 baselineLoads: ImageLoadRecord[];
 /** Convenience: averages + a rough p50 for a quick comparison. */
 summary(): {
 fireCount: number;
 prefetched: { count: number; avgMs: number; p50Ms: number; cacheHitRate: number };
 baseline: { count: number; avgMs: number; p50Ms: number };
 improvementMs: number;
 improvementPct: number;
 };
}

declare global {
 interface Window {
 __prefetchStats?: PrefetchStats;
 }
}

const IMAGE_HOST_HINT = /supabase\.co\/storage\//;

const stats: PrefetchStats = {
 fireCount: 0,
 prefetchedUrls: new Set<string>(),
 prefetchedLoads: [],
 baselineLoads: [],
 summary() {
 const avg = (xs: number[]) =>
 xs.length === 0 ? 0 : xs.reduce((a, b) => a + b, 0) / xs.length;
 const p50 = (xs: number[]) => {
 if (xs.length === 0) return 0;
 const sorted = [...xs].sort((a, b) => a - b);
 return sorted[Math.floor(sorted.length / 2)];
 };
 const pf = this.prefetchedLoads.map((r) => r.duration);
 const bl = this.baselineLoads.map((r) => r.duration);
 const cacheHits = this.prefetchedLoads.filter((r) => r.fromCache).length;
 const pfAvg = avg(pf);
 const blAvg = avg(bl);
 return {
 fireCount: this.fireCount,
 prefetched: {
 count: pf.length,
 avgMs: +pfAvg.toFixed(1),
 p50Ms: +p50(pf).toFixed(1),
 cacheHitRate: pf.length ? +(cacheHits / pf.length).toFixed(2) : 0,
 },
 baseline: {
 count: bl.length,
 avgMs: +blAvg.toFixed(1),
 p50Ms: +p50(bl).toFixed(1),
 },
 improvementMs: +(blAvg - pfAvg).toFixed(1),
 improvementPct: blAvg > 0 ? +(((blAvg - pfAvg) / blAvg) * 100).toFixed(1) : 0,
 };
 },
};

let observer: PerformanceObserver | null = null;

/** Initialise the observer once per page. Safe to call multiple times. */
export function initPrefetchTelemetry() {
 if (typeof window === "undefined") return;
 if (window.__prefetchStats) return;
 window.__prefetchStats = stats;

 if (typeof PerformanceObserver === "undefined") return;

 try {
 observer = new PerformanceObserver((list) => {
 for (const entry of list.getEntries()) {
 if (entry.entryType !== "resource") continue;
 const e = entry as PerformanceResourceTiming;
 // Only look at image-ish resources hitting the storage CDN. This
 // avoids noise from HTML/JS/JSON responses.
 if (e.initiatorType !== "img" && !IMAGE_HOST_HINT.test(e.name)) continue;

 // Cache hits report transferSize=0 (memory or disk cache) or a
 // `deliveryType` of `cache` in newer browsers.
 const fromCache =
 e.transferSize === 0 ||
 ("deliveryType" in e && (e as unknown as { deliveryType: string }).deliveryType === "cache");

 const record: ImageLoadRecord = {
 url: e.name,
 duration: +e.duration.toFixed(1),
 fromCache,
 ts: e.startTime,
 };

 if (stats.prefetchedUrls.has(e.name)) {
 stats.prefetchedLoads.push(record);
 } else {
 stats.baselineLoads.push(record);
 }
 }
 });
 observer.observe({ type: "resource", buffered: true });
 } catch {
 // PerformanceObserver may throw in older browsers; telemetry is best-effort.
 }
}

/**
 * Record that a ProductCard warmed a neighbor image. `url` MUST be the exact
 * URL the neighbor card will request (post-transform) so PerformanceObserver
 * can match the eventual resource load.
 */
export function trackPrefetchFire(url: string) {
 if (typeof window === "undefined") return;
 if (!window.__prefetchStats) initPrefetchTelemetry();
 stats.fireCount += 1;
 stats.prefetchedUrls.add(url);
 if (typeof performance !== "undefined" && "mark" in performance) {
 try {
 performance.mark(`prefetch:fire:${stats.fireCount}`, { detail: { url } });
 } catch {
 // Older browsers reject the mark options bag; the counter still works.
 }
 }
}
